import { useEffect, useState } from "react";
import { api, unwrap } from "@/lib/api";
import { Tag, Plus, X, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ACCENTS = [
  { k: "gold",    label: "Gold",    swatch: "#D4A853" },
  { k: "mauve",   label: "Mauve",   swatch: "#5E4060" },
  { k: "rose",    label: "Rose",    swatch: "#F0D5CE" },
  { k: "success", label: "Success", swatch: "#16A34A" },
  { k: "amber",   label: "Amber",   swatch: "#F59E0B" },
];

const ICONS = ["Tag", "Sparkles", "Gift", "Hand", "Heart", "Scissors", "Star"];

export function MarketingFlashesManager({ branchId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = () => {
    api.get("/marketing-flashes").then((r) => setItems(unwrap(r) || [])).catch(() => {});
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  const toggle = async (f) => {
    try {
      await api.patch(`/marketing-flashes/${f.id}`, { is_active: !f.is_active });
      refresh();
    } catch { toast.error("Could not update flash"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/marketing-flashes/${id}`);
      toast.success("Flash deleted");
      refresh();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="glass-primary rounded-[20px] overflow-hidden" data-testid="marketing-flashes-view">
      <button
        onClick={() => setOpen((x) => !x)}
        data-testid="marketing-flashes-toggle"
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/30 transition"
      >
        <div className="flex items-center gap-3">
          <Tag size={16} className="text-[var(--color-gold-dark)]" />
          <div>
            <div className="font-sora font-semibold text-[15px] text-[var(--color-text-primary)]">Marketing flashes</div>
            <div className="font-inter text-[12px] text-[var(--color-text-muted)]">
              Rotating offers shown on the public Waiting Display
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {items.length > 0 && <span className="font-mono text-[12px] text-[var(--color-text-muted)]">{items.filter((f) => f.is_active).length} active</span>}
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 anim-slide-up space-y-3">
          <div className="flex justify-end">
            <button data-testid="marketing-flashes-create-btn" onClick={() => setCreateOpen(true)} className="btn-3d btn-gold-3d btn-sm">
              <Plus size={14} /> New flash
            </button>
          </div>

          {items.length === 0 ? (
            <div className="font-inter text-[13px] text-[var(--color-text-muted)] text-center py-6">
              No flashes yet. Create one to show offers on the Waiting Display.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map((f) => {
                const accent = ACCENTS.find((a) => a.k === f.accent) || ACCENTS[0];
                return (
                  <div key={f.id} data-testid={`marketing-flashes-row-${f.id}`}
                       className="rounded-2xl p-4 border bg-white/55 border-white/80">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: accent.swatch }} />
                        <span className="font-sora font-semibold text-[14px]">{f.title}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="checkbox" checked={!!f.is_active} onChange={() => toggle(f)}
                                 data-testid={`marketing-flashes-toggle-${f.id}`}
                                 className="w-3.5 h-3.5 rounded border-[var(--color-mauve)]/40" />
                          <span className="font-inter text-[11px] text-[var(--color-text-muted)]">Active</span>
                        </label>
                        <button onClick={() => remove(f.id)} className="w-7 h-7 rounded-full glass-subtle flex items-center justify-center hover:bg-white/80"
                                data-testid={`marketing-flashes-delete-${f.id}`} aria-label="Delete">
                          <Trash2 size={11} className="text-[var(--color-danger-deep)]" />
                        </button>
                      </div>
                    </div>
                    {f.subtitle && <div className="font-inter text-[12px] text-[var(--color-text-secondary)] mt-1">{f.subtitle}</div>}
                    <div className="flex items-center gap-2 mt-2">
                      {f.code && <span className="font-mono text-[11px] px-2 py-0.5 rounded-full bg-[var(--color-mauve)]/10 text-[var(--color-mauve-deep)]">{f.code}</span>}
                      {f.valid_until && <span className="font-inter text-[11px] text-[var(--color-text-muted)]">Until {f.valid_until}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {createOpen && (
        <CreateFlashModal branchId={branchId} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); refresh(); }} />
      )}
    </div>
  );
}

function CreateFlashModal({ branchId, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", subtitle: "", code: "", valid_until: "",
    accent: "gold", icon: "Tag", is_active: true,
  });

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    try {
      await api.post("/marketing-flashes", {
        ...form,
        valid_until: form.valid_until || null,
        code: form.code.trim() || null,
        subtitle: form.subtitle.trim() || null,
        branch_id: branchId || null,
      });
      toast.success(`Flash "${form.title}" created`);
      onCreated?.();
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Could not create flash");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center anim-fade-in"
         style={{ background: "rgba(28,25,23,0.40)", backdropFilter: "blur(12px)" }}
         data-testid="marketing-flashes-create-modal">
      <div className="glass-elevated rounded-[24px] p-6 w-[min(520px,calc(100vw-32px))] max-h-[88vh] overflow-auto anim-slide-up-lg space-y-3">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="overline mb-1">New marketing flash</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">Promote an offer on the display</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>

        <Row label="Title *">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value.toUpperCase() })}
                 placeholder="5% OFF TODAY"
                 data-testid="marketing-flashes-title-input"
                 className={`${inputCls} uppercase`} />
        </Row>
        <Row label="Subtitle">
          <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                 placeholder="On every service"
                 data-testid="marketing-flashes-subtitle-input"
                 className={inputCls} />
        </Row>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Promo code (optional)">
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                   placeholder="GLOSS5"
                   data-testid="marketing-flashes-code-input"
                   className={`${inputCls} font-mono uppercase`} />
          </Row>
          <Row label="Valid until">
            <input type="date" value={form.valid_until}
                   onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                   data-testid="marketing-flashes-valid-input"
                   className={`${inputCls} font-mono`} />
          </Row>
        </div>

        <div>
          <div className="overline mb-1.5">Accent colour</div>
          <div className="flex flex-wrap gap-1.5">
            {ACCENTS.map((a) => (
              <button key={a.k} type="button" onClick={() => setForm({ ...form, accent: a.k })}
                      data-testid={`marketing-flashes-accent-${a.k}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-inter font-medium border transition ${form.accent === a.k ? "bg-[var(--color-mauve)]/12 border-[var(--color-mauve)]/40 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: a.swatch }} />
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <Row label="Icon">
          <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  data-testid="marketing-flashes-icon-select" className={inputCls}>
            {ICONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Row>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-3d btn-glass-3d btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-3d btn-gold-3d" onClick={submit} data-testid="marketing-flashes-submit-btn">
            <Plus size={14} /> Create flash
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[14px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-gold-mid)] focus:ring-2 focus:ring-[var(--color-gold-mid)]/20 transition";

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)] mb-1.5">{label}</div>
      {children}
    </label>
  );
}

export default MarketingFlashesManager;
